import { usePropostasListarKanban } from "services/api/Funil_Propostas/kanban-listar-get";
import { useState, useEffect } from "react";
import { Skeleton, CircularProgress, Tooltip } from "@mui/material";
import { RootStateOrAny, useSelector } from "react-redux";
import { AuthState } from "store/ducks/auth/types";
import { format } from "date-fns";
import * as S from "./styles";
import ItemCard from "./components/ItemCard";

interface Data {
  codigoEtapa: string;
  nomeEtapa: string;
  searchValue: string;
  filterAction: boolean;
  setFilterAction: (value: boolean) => void;
  selectedEtapa: any;
  selectedSubetapa: any;
  selectedEmpreendimento: any;
  selectedTipoFiltroData: any;
  dataInicio: Date | null;
  dataFim: Date | null;
  onQuantidadeLoaded?: (
    codigo: string,
    quantidade: number,
    paginaAtual: number
  ) => void;
}

const EtapaCard = (data: Data) => {
  const { usuarioComercial, name, apelido } = useSelector<
    RootStateOrAny,
    AuthState
  >((state) => state.user);

  const toApiDate = (date: Date | null) => {
    if (!date) return undefined;
    return format(date, "yyyy-MM-dd");
  };

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [items, setItems] = useState<any[]>([]);
  const [quantidadeEtapa, setQuantidadeEtapa] = useState<string>("0");
  const [vgv, setVgv] = useState<string>("0");

  const {
    data: dataKanbanListar,
    refetch: refetchKanbanListar,
    isFetching: isFetchingKanbanListar,
  } = usePropostasListarKanban({
    queryKey: `${data.nomeEtapa}-${paginaAtual}`,
    codigo: data.codigoEtapa,
    Etapa: data.selectedEtapa?.value || "",
    tipoFiltroData: data.selectedTipoFiltroData
      ? Number(data.selectedTipoFiltroData.value)
      : undefined,
    dataInicio: data.selectedTipoFiltroData
      ? toApiDate(data.dataInicio)
      : undefined,
    dataFim: data.selectedTipoFiltroData ? toApiDate(data.dataFim) : undefined,
    SubEtapa:
      data.selectedSubetapa?.map((subEtapa: any) => subEtapa.value) || [],
    IdEmpreendimento:
      data.selectedEmpreendimento?.map(
        (empreendimento: any) => empreendimento.value
      ) || [],
    Comercial: true,
    PalavraChave: data.searchValue,
    PaginaAtual: paginaAtual,
    TotalItensRetorno: 3,
  });

  useEffect(() => {
    if (dataKanbanListar && !isFetchingKanbanListar) {
      const qtd = dataKanbanListar.data?.quantidadeEtapa;
      const vgvValue = dataKanbanListar.data?.vgv;
      if (qtd !== undefined) setQuantidadeEtapa(String(qtd));
      if (vgvValue !== undefined) setVgv(String(vgvValue || "0"));
      data.onQuantidadeLoaded?.(data.codigoEtapa, qtd || 0, paginaAtual);
    }
  }, [dataKanbanListar, isFetchingKanbanListar]);

  useEffect(() => {
    if (data.filterAction) {
      setPaginaAtual(1);
      setItems([]);
      refetchKanbanListar();
      data.setFilterAction(false);
    }
  }, [data.filterAction]);

  useEffect(() => {
    if (paginaAtual > 1) {
      refetchKanbanListar();
    }
  }, [paginaAtual]);

  useEffect(() => {
    if (dataKanbanListar && !isFetchingKanbanListar) {
      const novaListaFila = dataKanbanListar.data?.listaFila ?? [];
      if (paginaAtual === 1) {
        setItems(novaListaFila);
      } else {
        setItems((prevItems) => [...prevItems, ...novaListaFila]);
      }
    }
  }, [dataKanbanListar, isFetchingKanbanListar, paginaAtual]);

  const loadMoreItems = () => {
    if (items.length < (dataKanbanListar?.pagination?.totalItems || 0)) {
      setPaginaAtual((prev) => prev + 1);
    }
  };

  const handleScroll = (event: any) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 5 &&
      !isFetchingKanbanListar
    ) {
      loadMoreItems();
    }
  };

  const handleRefreshColumn = () => {
    setItems([]);
    if (paginaAtual !== 1) {
      setPaginaAtual(1);
      return;
    }
    refetchKanbanListar();
  };

  const responsavelAtualNome = apelido || name || "";

  const handleCaptureSuccess = (idOportunidade: string) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.idOportunidade === idOportunidade
          ? { ...item, responsavelAnaliseAtual: responsavelAtualNome }
          : item
      )
    );
  };

  const isEtapaComercial = data.nomeEtapa
    ?.normalize("NFD")
    ?.replace(/[\u0300-\u036f]/g, "")
    ?.toLowerCase()
    ?.includes("comercial");

  const showCaptureAction = Boolean(usuarioComercial && isEtapaComercial);

  return (
    <S.Container onScroll={handleScroll}>
      <S.Header>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Tooltip title={data.nomeEtapa}>
            <S.TextEtapa
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                maxWidth: "100px",
              }}
            >
              {data.nomeEtapa}
            </S.TextEtapa>
          </Tooltip>
          <S.TextEtapa>({quantidadeEtapa})</S.TextEtapa>
        </div>
        <div>
          <S.TextEtapa>VGV {vgv}</S.TextEtapa>
        </div>
      </S.Header>
      <S.SpaceY space={12} />
      {isFetchingKanbanListar && items.length === 0 && (
        <S.BodySkeleton>
          <Skeleton variant="rounded" width={271} height={340} />
        </S.BodySkeleton>
      )}
      {items.map((item: any) => (
        <ItemCard
          key={item.numeroCotacao}
          listaFila={item}
          isFetchingKanbanListar={isFetchingKanbanListar}
          showCaptureAction={showCaptureAction}
          onCaptureSuccess={handleCaptureSuccess}
        />
      ))}
      {isFetchingKanbanListar && items.length > 0 && (
        <S.LoadingContainer>
          <CircularProgress size={24} />
        </S.LoadingContainer>
      )}
      <S.SpaceY space={12} />
    </S.Container>
  );
};

export default EtapaCard;
